import { RacemapColors } from '@racemap/utilities/consts/common';
import { shortIdBuilder } from '@racemap/utilities/functions/utils';
import type { Feature, FeatureCollection, LineString, Point, Polygon } from 'geojson';
import type { Immutable } from 'immer';
import { type FC, useContext, useEffect, useMemo, useRef } from 'react';
import { Layer, type MapMouseEvent, Source } from 'react-map-gl/mapbox';
import { MapContext, type NewDataProps } from '../BasicMap/BasicMap';
import { convertLabelFeaturesToCollection, injectFeatureIds } from '../helpers';

export type LabelProperties = {
  name?: string;
  shortName?: string;
  color?: string | null;
};

type Geometry = Point | LineString | Polygon;

export type LabelFeatures =
  | Feature<Geometry, LabelProperties>
  | Array<Feature<Geometry, LabelProperties>>
  | FeatureCollection<Geometry, LabelProperties>;

export interface Props {
  labelFeatures: LabelFeatures | Immutable<LabelFeatures>;
  color?: string;
  beforeId?: string;
  onNewData?: (props: NewDataProps) => void;
  onMouseEnter?: (evt: MapMouseEvent) => void;
  onMouseLeave?: (evt: MapMouseEvent) => void;
  onClick?: (evt: MapMouseEvent) => void;
  autofocus?: boolean;
  id?: string;
  maxZoom?: number;
  minZoom?: number;
}

export const LabelLayer: FC<Props> = ({
  labelFeatures,
  color = RacemapColors.PaleBlue,
  beforeId,
  onNewData,
  autofocus = false,
  onMouseEnter,
  onMouseLeave,
  onClick,
  id,
  maxZoom = 24,
  minZoom = 0,
}) => {
  const idRef = useRef(id || shortIdBuilder());
  const { features, layout, paint } = useMemo(
    () => convertLabelFeaturesToCollection(labelFeatures, color),
    [labelFeatures, color],
  );
  const featuresWithIdProperty = useMemo(() => injectFeatureIds(features), [features]);
  const featureCollection: FeatureCollection<Point, LabelProperties> = {
    type: 'FeatureCollection',
    features: [...featuresWithIdProperty],
  };
  const {
    setMouseEnterAction,
    setMouseLeaveAction,
    setOnClickAction,
    addInteractiveLayerIds,
    mapStyle,
  } = useContext(MapContext);
  const layers = mapStyle?.layers.map((l) => l.id) || [];

  useEffect(() => {
    addInteractiveLayerIds(`layer-point-${idRef.current}`);
  });

  useEffect(() => {
    onNewData?.({ layer: `layer-point-${idRef.current}`, data: featureCollection, autofocus });
  }, [labelFeatures]);

  useEffect(() => {
    if (onMouseEnter != null) {
      setMouseEnterAction(`point-${idRef.current}`, onMouseEnter);
    }
    if (onMouseLeave != null) {
      setMouseLeaveAction(`point-${idRef.current}`, onMouseLeave);
    }
    if (onClick != null) {
      setOnClickAction(`point-${idRef.current}`, onClick);
    }
  }, [onMouseEnter, onMouseLeave, onClick]);

  return (
    <Source
      type="geojson"
      data={featureCollection}
      id={`labels-${idRef.current}`}
      key={`labels-${idRef.current}`}
      promoteId={'_id'}
    >
      <Layer
        id={`layer-labels-${idRef.current}`}
        key={`layer-labels-${idRef.current}`}
        type="symbol"
        layout={layout}
        maxzoom={maxZoom}
        minzoom={minZoom}
        beforeId={beforeId != null && layers.includes(beforeId) ? beforeId : undefined}
        paint={paint}
      />
    </Source>
  );
};
